import { buildBillablePromotionAmounts } from "src/helper/promotionItemAttribution";

const isCashDiscountDetail = (item) =>
  Boolean(item?.isCashDiscount) || item?.discountCode === "CD";

const amountOf = (item) => Math.abs(Number(item?.discountAmount) || 0);

const namedDiscount = (item) => {
  const name = String(item?.discountCode || "").trim();
  if (!name || name.toLowerCase() === "discount") {
    return "";
  }
  return name;
};

/**
 * Same discount section as the POS check and receipt:
 * item total, each order discount, then the cash discount.
 * Coupons stay on their own lines.
 */
export function buildReceiptDiscountLines(details) {
  let itemTotal = 0;
  let cashTotal = 0;
  const orderLines = [];

  for (const item of details || []) {
    const amount = amountOf(item);
    if (!amount || item?.couponId || isCashDiscountDetail(item)) {
      continue;
    }
    if (item.appliesTo === "item") {
      itemTotal += amount;
    }
  }

  for (const item of details || []) {
    const amount = amountOf(item);
    if (!amount || item?.couponId) {
      continue;
    }
    if (isCashDiscountDetail(item)) {
      cashTotal += amount;
    } else if (item.appliesTo !== "item") {
      const name = namedDiscount(item);
      orderLines.push({
        label: name ? `Discount (${name}) - Order` : "Discount - Order",
        amount,
      });
    }
  }

  const lines = [];
  if (itemTotal) {
    lines.push({ label: "Discount - Items", amount: itemTotal });
  }
  lines.push(...orderLines);
  if (cashTotal) {
    lines.push({ label: "Discount (CD)", amount: cashTotal });
  }
  return lines;
}

export function buildCouponLines(details) {
  return (details || [])
    .map((item) => {
      if (!item?.couponId) {
        return null;
      }
      const amount = amountOf(item);
      if (!amount) {
        return null;
      }
      const name = item?.discountCode || item?.couponName || item?.couponCode || "";
      return {
        key: item?.uuid || item?.couponId || name,
        label: name ? `Coupon (${name}):` : "Coupon:",
        amount,
      };
    })
    .filter(Boolean);
}

export function buildPromotionLines(promotions, items) {
  const billable = items?.length
    ? buildBillablePromotionAmounts(items, promotions)
    : null;
  return (promotions || [])
    .map((promotion, index) => {
      const amount = billable ? billable[index] : amountOf(promotion);
      if (!amount) {
        return null;
      }
      const name = promotion?.displayLabel || promotion?.promotionName || "Promotion";
      return {
        key: promotion?.uuid || promotion?.promotionUuid || `promotion-${index}`,
        label: `Promotion (${name})`,
        amount,
      };
    })
    .filter(Boolean);
}

/** Item row label used on the POS check: "Discount (name) - Item (10%)". */
export function itemCheckDiscountLabel(item) {
  if (!item) {
    return "";
  }
  const cash = isCashDiscountDetail(item);
  let label = item?.couponId ? "Coupon" : "Discount";
  const name = item?.discountCode || item?.couponName;
  if (name && String(name).toLowerCase() !== "discount") {
    label += ` (${name})`;
  }
  if (!cash) {
    label += " - Item";
  }
  if (item?.discountType === "percentage" && item?.discountValue) {
    label += ` (${item.discountValue}%)`;
  }
  return label;
}
